import { ReadonlyVec3 } from 'gl-matrix';
import {
    domainEventBus,
    ROI_CONTOUR_EDIT_EVENT_TYPES,
    RoiContourEditEvent,
} from '~/domain/events';
import { DomainEventSubscription } from '~/domain/events/DomainEventBus';
import { Observer } from '~/engine/core/observable';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import { Grid } from '~/engine/data/common/Grid';
import type { Environment } from '~/engine/data/Environment';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import {
    RegionOfInterest,
    TextureWrite,
} from '~/engine/data/structure/RegionOfInterest';
import {
    resolveImageSlice,
    resolveTextureSlice,
} from '~/engine/data/structure/planeSliceResolution';
import type { Tool } from '~/engine/tool/Tool';
import { BrushTool } from '~/engine/tool/tools/contouring-tools/BrushTool';
import { isViewDrawable } from '~/engine/tool/tools/contouring-tools/obliqueGating';
import {
    assertGlWorkLanded,
    discardPendingGlErrors,
} from '~/engine/util/gl/GLUtilities';
import type { EnvironmentViews } from '~/engine/view/EnvironmentViews';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { viewShowsAcquiredPrimarySlice } from '~/engine/view/planeViewGeometry';
import { ViewManager } from '~/engine/view/ViewManager';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { extractSlicePolygons } from '~/engine/core/utils/marchingSquares';
import {
    blendSlicesBSpline,
    CONTOUR_AUTO_SUGGESTION_OPERATION,
    CONTOUR_AUTO_SUGGESTION_PREVIEW_DELAY_MS,
    NEAREST_CONTOUR_COPY_OPERATION,
    SliceWithPosition,
} from './sliceBlending';
import {
    ContouredRangePosition,
    ContourOperations,
} from '~/engine/util/algorithms/sdf/ContourOperations';
import {
    EdgeRefinementOptions,
    estimateRobustIntensitySpread,
    extractIntensitySliceAlignedToGrid,
    refineSliceWithIntensityEdge,
    refineSliceWithIntensityRegion,
    RegionRefinementOptions,
    scaleRefinementOptionsForSpread,
} from '~/engine/util/algorithms/sdf/IntensityRefinement';
import { Modality } from '~/domain/dicom/modality';
import {
    ContourAutoSuggestionPreviewRenderer,
    ContourAutoSuggestionPreviewState,
} from './ContourAutoSuggestionPreviewRenderer';
import { lightenColor } from '~/utils/colorUtils';
import { CatalogMessageId, localization } from '~/i18n';
import { enqueueSnackbar } from '~/utils/notifications';

type ActiveSuggestion = {
    roi: RegionOfInterest;
    /** In the image's grid, which the texture moving does not change. */
    imageSliceZ: number;
    kind: 'interpolated' | 'copied';
    /**
     * The exact pixel data the renderer used to produce the
     * displayed ghost — a blend of the neighbouring slices, or a
     * verbatim copy of the nearest contoured one. {@link accept}
     * writes these bytes so the committed contour matches what the
     * user saw.
     */
    sourceSliceBytes: Uint8Array;
    /** The ghost as drawn, so a view that joins while it is pending shows it. */
    preview: ContourAutoSuggestionPreviewState;
};

/** Where a suggestion lands in the current texture, extended as needed. */
type DerivedSuggestion = Omit<ActiveSuggestion, 'preview'> & {
    textureZ: number;
    extension: { before: number; after: number };
};

/**
 * Subscribes `listener` to every change of which image of a fused pair a
 * view of the edited Environment draws, and returns the unsubscribe.
 */
export type FusedImageDisplaySubscription = (
    listener: () => void
) => () => void;

/**
 * Singleton controller for contour auto-suggest: on a displayed slice
 * where the active structure has no contour, it suggests one — between
 * the structure's first and last contoured slice, a blend of the
 * neighbouring slices refined against the image; beyond them, on any
 * slice of the image, a verbatim copy of the nearest contoured slice.
 * Lifecycle:
 *
 *  1. {@link enable} is called by a React hook when the toggle is
 *     activated, given the {@link Environment} whose structure is
 *     edited, the live set of views that show it, and a resolver
 *     returning the currently active {@link RegionOfInterest}. A
 *     suggestion is made, and accepted, only while one of those views
 *     displays an image slice: it shows the primary image in its
 *     acquisition plane, so a rotated view or a patient-aligned view of
 *     an obliquely acquired image gets no suggestion and cannot commit
 *     a pending one.
 *  2. The controller subscribes to:
 *      - every contour edit of the active structure, undo and redo
 *        included, so a stale ghost is discarded and the
 *        re-evaluation can pick up an updated source slice;
 *      - the Environment's plane-intersection observer, scheduling a
 *        delayed evaluation whenever the user lands on a new slice;
 *      - the Environment's pan/zoom observer, refreshing the ghost so
 *        it stays anchored to patient coordinates;
 *      - the view set, so a view that joins gets the ghost and a view
 *        that leaves is let go of;
 *      - which image of the fused pair each view draws, so a view that
 *        turns to the secondary alone is let go of and one that turns
 *        back gets the ghost;
 *      - document-level capture-phase keydown / mousedown listeners
 *        for the approval gestures, bubble-phase mousedown / mouseup
 *        listeners that hold the ghost off during a brush stroke, and
 *        a bubble-phase mousemove listener that repaints the ghost
 *        after tools that clear the shared 2D overlay (see below) and
 *        offers it again after a stroke the brush abandoned.
 *     The ghost is drawn on, and accepted from, every view of the
 *     Environment that draws on the image's slices and shows the primary
 *     image ({@link viewShowsAcquiredPrimarySlice}), since the
 *     suggestion is shaped by the primary's slices and pixels; a
 *     sagittal or coronal view, or a tile of another Environment, never
 *     shows it and a click there never accepts it. A Ctrl+click on a
 *     view that draws on the slices but shows the fused secondary alone
 *     is refused with the reason. Which views those are is re-read when
 *     the set changes, when a view's fused display changes and when the
 *     plane-intersection point moves, which is also what a view
 *     re-oriented in place notifies.
 *  3. The interaction model on a rendered suggestion:
 *      - **accept** ({@link accept}) on `Ctrl + left-click`: commits
 *        the contour as one undoable edit and stays on the current
 *        slice; the only way a suggestion becomes contour. The
 *        capture-phase listener consumes the click so the active tool
 *        does not also start an action.
 *      - a brush stroke started on the slice withdraws the ghost, so
 *        the stroke commits only what it draws; the ghost is derived
 *        again once the stroke has ended.
 *      - **discard** ({@link discardPending}) on `Esc`, or
 *        automatically on slice change / disable.
 *  4. {@link disable} clears all subscriptions, the pending timer
 *     and any rendered ghost.
 *
 * Suggestions are held entirely in-memory until accepted (or
 * discarded by navigation / Esc).
 */
export class ContourAutoSuggestionController {
    private static instance: ContourAutoSuggestionController | undefined;

    public static getInstance(): ContourAutoSuggestionController {
        if (!ContourAutoSuggestionController.instance) {
            ContourAutoSuggestionController.instance =
                new ContourAutoSuggestionController();
        }
        return ContourAutoSuggestionController.instance;
    }

    private enabled = false;
    private environment: Environment | undefined;
    private views: EnvironmentViews | undefined;
    private getRoi: (() => RegionOfInterest | undefined) | undefined;
    private getActiveTool: (() => Tool | undefined) | undefined;

    /**
     * One entry per bound view: a view of the Environment that draws on
     * the image's slices. The value is its renderer, or `undefined` for a
     * view whose 2D overlay could not be drawn on, which stays bound so it
     * is not retried on every re-sync and is never accepted from, since
     * the user sees no ghost there.
     */
    private readonly renderers = new Map<
        PlaneView,
        ContourAutoSuggestionPreviewRenderer | undefined
    >();

    private pending: ActiveSuggestion | undefined;
    private pendingTimer: ReturnType<typeof setTimeout> | undefined;

    /** Set while a brush stroke holds the suggestion off. */
    private reevaluateAfterStroke = false;

    private contourSubscriptions: DomainEventSubscription[] = [];
    private unsubscribeViews: (() => void) | undefined;
    private unsubscribeFusedImageDisplay: (() => void) | undefined;
    private planeIntersectionObserver: Observer<ReadonlyVec3> | undefined;
    private panZoomObserver: Observer<number> | undefined;
    private keydownHandler: ((e: KeyboardEvent) => void) | undefined;
    private mousedownHandler: ((e: MouseEvent) => void) | undefined;
    private pressHandledHandler: (() => void) | undefined;
    private releaseHandler: (() => void) | undefined;
    private mousemoveHandler: ((e: MouseEvent) => void) | undefined;

    private constructor() {}

    public isEnabled(): boolean {
        return this.enabled;
    }

    public hasPendingSuggestion(): boolean {
        return this.pending !== undefined;
    }

    /**
     * @param environment The Environment whose structure is edited; the
     *   slice, the pan/zoom and the image are read from it.
     * @param views The live set of views showing that Environment; the
     *   ghost follows the drawable ones as they join and leave.
     * @param subscribeFusedImageDisplay Follows which image of the fused
     *   pair each of those views draws.
     * @param getRoi The structure a suggestion is made for.
     * @param getActiveTool The tool the user is working with, if any.
     */
    public enable(
        environment: Environment,
        views: EnvironmentViews,
        subscribeFusedImageDisplay: FusedImageDisplaySubscription,
        getRoi: () => RegionOfInterest | undefined,
        getActiveTool: () => Tool | undefined
    ): void {
        if (this.enabled) {
            this.disable();
        }
        this.enabled = true;
        this.environment = environment;
        this.views = views;
        this.getRoi = getRoi;
        this.getActiveTool = getActiveTool;

        this.unsubscribeViews = views.subscribe(() => this.syncRenderers());
        this.unsubscribeFusedImageDisplay = subscribeFusedImageDisplay(() =>
            this.syncRenderers()
        );
        this.syncRenderers();

        this.contourSubscriptions = ROI_CONTOUR_EDIT_EVENT_TYPES.map((type) =>
            domainEventBus.subscribe(type, this.onContourEdit.bind(this))
        );

        this.planeIntersectionObserver = (_value: ReadonlyVec3) => {
            this.onPlaneIntersectionChanged();
        };
        environment.planeIntersectionPoint.subscribe(
            this.planeIntersectionObserver
        );

        this.panZoomObserver = (_value: number) => {
            this.refreshRenderers();
        };
        environment.panZoom.subscribe(this.panZoomObserver);

        // Approval gestures — keydown / mousedown registered in the
        // capture phase so they preempt the engine's bubble-phase
        // canvas handlers (ViewManager.handleEvent), letting Ctrl+click
        // commit before the active tool starts its own action and
        // preserving existing Esc behaviours when no suggestion is
        // pending.
        this.keydownHandler = this.onDocumentKeyDown.bind(this);
        this.mousedownHandler = this.onDocumentMouseDown.bind(this);
        document.addEventListener('keydown', this.keydownHandler, true);
        document.addEventListener('mousedown', this.mousedownHandler, true);
        // In the bubble phase, after the engine: whether a press started
        // a brush stroke is known only once the brush has handled it.
        this.pressHandledHandler = this.onPressHandled.bind(this);
        this.releaseHandler = this.resumeAfterStroke.bind(this);
        document.addEventListener('mousedown', this.pressHandledHandler);
        document.addEventListener('mouseup', this.releaseHandler);

        // Mouse-move repaint registered in the BUBBLE phase so it runs
        // AFTER ViewManager has driven the active tool's render. Tools
        // such as line-deform render their own cursor on the shared 2D
        // overlay and clear the whole canvas first, which would erase
        // the ghost; repainting afterwards keeps the suggestion visible
        // on top of the tool's overlay.
        this.mousemoveHandler = this.onDocumentMouseMove.bind(this);
        document.addEventListener('mousemove', this.mousemoveHandler, false);

        // Evaluate once on enable so a suggestion shows up if the
        // user is already on an empty slice with a populated
        // neighbour, without having to scroll first.
        this.scheduleEvaluation();
    }

    public disable(): void {
        this.enabled = false;
        this.reevaluateAfterStroke = false;

        this.cancelPendingTimer();

        this.contourSubscriptions.forEach((s) => s.unsubscribe());
        this.contourSubscriptions = [];

        if (this.unsubscribeViews) {
            this.unsubscribeViews();
            this.unsubscribeViews = undefined;
        }
        if (this.unsubscribeFusedImageDisplay) {
            this.unsubscribeFusedImageDisplay();
            this.unsubscribeFusedImageDisplay = undefined;
        }

        if (this.environment) {
            if (this.planeIntersectionObserver) {
                this.environment.planeIntersectionPoint.unsubscribe(
                    this.planeIntersectionObserver
                );
            }
            if (this.panZoomObserver) {
                this.environment.panZoom.unsubscribe(this.panZoomObserver);
            }
        }
        this.planeIntersectionObserver = undefined;
        this.panZoomObserver = undefined;

        if (this.keydownHandler) {
            document.removeEventListener('keydown', this.keydownHandler, true);
            this.keydownHandler = undefined;
        }
        if (this.mousedownHandler) {
            document.removeEventListener(
                'mousedown',
                this.mousedownHandler,
                true
            );
            this.mousedownHandler = undefined;
        }
        if (this.pressHandledHandler && this.releaseHandler) {
            document.removeEventListener('mousedown', this.pressHandledHandler);
            document.removeEventListener('mouseup', this.releaseHandler);
            this.pressHandledHandler = this.releaseHandler = undefined;
        }
        if (this.mousemoveHandler) {
            document.removeEventListener(
                'mousemove',
                this.mousemoveHandler,
                false
            );
            this.mousemoveHandler = undefined;
        }

        for (const renderer of this.renderers.values()) {
            renderer?.dispose();
        }
        this.renderers.clear();

        this.discardPending();

        this.environment = undefined;
        this.views = undefined;
        this.getRoi = undefined;
        this.getActiveTool = undefined;
    }

    /**
     * Brings the renderers in line with the views that currently show the
     * suggestion ({@link viewShowsAcquiredPrimarySlice}): a view that joined gets a
     * renderer and the pending ghost, a view that left, turned away or
     * turned to the secondary alone has its renderer disposed and is not
     * painted again. Cheap enough to run per signal: one walk of the set
     * and one map lookup per view.
     */
    private syncRenderers(): void {
        if (!this.views) {
            return;
        }
        const drawable = this.views.current.filter(
            viewShowsAcquiredPrimarySlice
        );
        for (const [view, renderer] of this.renderers) {
            if (!drawable.includes(view)) {
                renderer?.dispose();
                this.renderers.delete(view);
            }
        }
        for (const view of drawable) {
            if (this.renderers.has(view)) {
                continue;
            }
            const renderer = this.createRenderer(view);
            this.renderers.set(view, renderer);
            if (renderer && this.pending) {
                renderer.setState(this.pending.preview);
            }
        }
    }

    private createRenderer(
        view: PlaneView
    ): ContourAutoSuggestionPreviewRenderer | undefined {
        try {
            return new ContourAutoSuggestionPreviewRenderer(view);
        } catch (err) {
            console.warn(
                'ContourAutoSuggestionController: cannot create renderer',
                err
            );
            return undefined;
        }
    }

    private paintRenderers(preview: ContourAutoSuggestionPreviewState): void {
        for (const renderer of this.renderers.values()) {
            renderer?.setState(preview);
        }
    }

    private refreshRenderers(): void {
        for (const renderer of this.renderers.values()) {
            renderer?.refresh();
        }
    }

    private clearRenderers(): void {
        for (const renderer of this.renderers.values()) {
            renderer?.clear();
        }
    }

    /** The bound view whose engine canvas is `target`, if any. */
    private boundViewWithCanvas(
        target: EventTarget | null
    ): PlaneView | undefined {
        if (!target) {
            return undefined;
        }
        for (const [view, renderer] of this.renderers) {
            if (renderer && view.canvas === target) {
                return view;
            }
        }
        return undefined;
    }

    /**
     * Whether `target` is the engine canvas of a view of the Environment
     * that draws on the image's slices but does not show the primary
     * image: a view the suggestion is withheld from, not one it never
     * applies to.
     */
    private isWithheldViewCanvas(target: EventTarget | null): boolean {
        return (
            !!target &&
            !!this.views?.current.some(
                (view) =>
                    view.canvas === target &&
                    isViewDrawable(view) &&
                    !viewShowsAcquiredPrimarySlice(view)
            )
        );
    }

    /**
     * Commit the pending suggestion, exactly as shown, as one undoable
     * edit, and trigger a viewport redraw. Returns `true` when written;
     * the pending suggestion is cleared either way. A suggestion the
     * structure no longer yields is refused. The write is staged on a
     * texture of its own, built from the structure's GPU data and
     * extended along Z where the slice lies beyond the structure's, and
     * swapped in once it has landed: the structure's texture is never
     * written, so a failure leaves the structure as it was. Both are
     * reported to the user.
     * Does NOT navigate — the view stays on the current slice.
     */
    public accept(): boolean {
        const accepted = this.pending;
        this.discardPending();
        if (!accepted) {
            return false;
        }
        const current = this.deriveSuggestion();
        const texture = accepted.roi.texture;
        if (
            !current ||
            !texture ||
            current.roi !== accepted.roi ||
            current.imageSliceZ !== accepted.imageSliceZ ||
            current.kind !== accepted.kind ||
            !sameBytes(current.sourceSliceBytes, accepted.sourceSliceBytes)
        ) {
            return this.refuse('sliceDeformSuggestOutOfDate', 'warning');
        }

        const { roi } = accepted;
        const { textureZ, extension } = current;
        const { before, after } = extension;
        const grid = texture.textureGrid;
        let modify: TextureWrite | undefined;
        let replacement: ContourTexture | undefined;
        try {
            discardPendingGlErrors();
            modify = roi.startModify(
                accepted.kind === 'copied'
                    ? NEAREST_CONTOUR_COPY_OPERATION
                    : CONTOUR_AUTO_SUGGESTION_OPERATION,
                { restoredOnFailure: true }
            );
            // Read from the GPU, not the pixel cache, so that the check
            // below covers this very readback: WebGL rejects one without
            // throwing and leaves it zero-filled.
            const held = texture.signedDistanceField.toArray();
            assertGlWorkLanded('ContourAutoSuggestionController: read');
            // Every slice keeps its bytes and patient position; the
            // slices gained are empty.
            const empty = () =>
                new Uint8Array(accepted.sourceSliceBytes.length);
            const slices = [
                ...Array.from({ length: before }, empty),
                ...held,
                ...Array.from({ length: after }, empty),
            ];
            slices[textureZ + before] = accepted.sourceSliceBytes.slice();
            replacement = texture.cloneWithNewSdf(slices, {
                grid:
                    before + after
                        ? grid
                              .expandOrShrink(0, 0, before, true)
                              .expandOrShrink(0, 0, after - before, false)
                        : grid,
                adoptPixels: true,
            });
            // WebGL reports a failed allocation or upload without
            // throwing: checked before the structure takes the texture
            // and before the edit is recorded.
            assertGlWorkLanded('ContourAutoSuggestionController: commit');
            roi.texture = replacement;
            modify.finish();
        } catch (err) {
            if (replacement && roi.texture === replacement) {
                roi.texture = texture;
            }
            replacement?.delete();
            // Nothing read during the failed accept stays cached: a
            // rejected readback is zero-filled.
            texture.invalidateCachedPixels();
            // startModify opens the modification before it snapshots the
            // structure, so one that throws leaves no write to cancel.
            if (modify) {
                modify.cancel();
            } else {
                roi.resetModificationFlag();
            }
            // The error's name only: its message can name the structure.
            console.error(
                `ContourAutoSuggestionController: failed to commit suggestion (${
                    err instanceof Error ? err.name : 'unknown error'
                })`
            );
            return this.refuse('sliceDeformSuggestNotAdded', 'error');
        }
        texture.delete();

        ViewManager.invalidate(LayerBit.CONTOUR_LAYER_BIT, roi.boundingBox);
        return true;
    }

    /** Tells the user an accept wrote nothing, and offers a fresh ghost. */
    private refuse(
        message: CatalogMessageId,
        variant: 'warning' | 'error'
    ): false {
        enqueueSnackbar(localization.t(message), { variant });
        this.scheduleEvaluation();
        return false;
    }

    /**
     * Handle a change to the Environment's plane-intersection point.
     *
     * Plane-intersection updates fire not only on slice scrolls but
     * also on intra-slice movements (crosshair drag, hover-driven
     * snapping, sub-voxel adjustments) and on a view re-oriented in
     * place, so the bound views are re-read first. When the visible
     * slice has NOT changed we keep the current ghost and just repaint
     * it (cheap), so the suggestion does not flicker away on every
     * mouse move. Only an actual slice change tears the ghost down
     * and schedules a fresh evaluation.
     */
    private onPlaneIntersectionChanged(): void {
        if (!this.enabled) {
            return;
        }
        this.syncRenderers();
        if (
            this.pending &&
            this.displayedImageSliceZ() === this.pending.imageSliceZ
        ) {
            this.refreshRenderers();
            return;
        }
        this.scheduleEvaluation();
    }

    /**
     * Document-level mousemove handler (bubble phase). Repaints the
     * pending ghost so it survives tools that clear the shared 2D
     * overlay on every move (e.g. line-deform draws its cursor after
     * a full `clearRect`), and offers the suggestion again after a
     * stroke the brush abandoned on this move.
     */
    private onDocumentMouseMove(_e: MouseEvent): void {
        this.resumeAfterStroke();
        if (this.pending) {
            this.refreshRenderers();
        }
    }

    /**
     * The plane displayed by the views that show the primary image in its
     * acquisition plane ({@link viewShowsAcquiredPrimarySlice}): through
     * the Environment's plane-intersection point, along such a view's
     * direction. Undefined when no view shows it.
     */
    private displayedPlane(): PlaneMm | undefined {
        const view = this.views?.current.find(viewShowsAcquiredPrimarySlice);
        return view && this.environment
            ? {
                  point: this.environment.planeIntersectionPoint.coordinates,
                  normal: view.viewDirection,
              }
            : undefined;
    }

    /** The displayed slice's index in the image's grid, if it has one. */
    private displayedImageSliceZ(): number | undefined {
        const image = this.environment?.image;
        const plane = this.displayedPlane();
        return image && plane ? resolveImageSlice(image, plane) : undefined;
    }

    /**
     * Document-level keydown handler (capture phase). Consumes the
     * `Esc` key to discard a pending suggestion. When no suggestion
     * is active, the event passes through untouched so existing
     * Esc behaviours (e.g. brush stroke cancellation) keep working.
     *
     * Modifier-only Esc — any combination with Ctrl/Alt/Shift/Meta is
     * ignored to avoid colliding with browser or OS shortcuts.
     */
    private onDocumentKeyDown(e: KeyboardEvent): void {
        if (!this.pending) {
            return;
        }
        if (e.key !== 'Escape') {
            return;
        }
        if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.discardPending();
    }

    /**
     * Document-level mousedown handler (capture phase):
     * `Ctrl + left-click` on the canvas of a view the ghost is drawn on,
     * while a suggestion is pending, **accepts** it. The click is
     * consumed (`preventDefault` + `stopPropagation`) so the active tool
     * does not also start an action; the suggestion is committed and the
     * view stays on the current slice.
     *
     * A `Ctrl + left-click` on a view the suggestion is withheld from
     * (one that shows the fused secondary alone) is consumed and refused
     * with the reason, so the accept the user asked for is neither lost
     * in silence nor taken as the active tool's press. Every other press
     * passes through untouched.
     *
     * Capture phase guarantees this runs before
     * `ViewManager.handleEvent`'s bubble-phase canvas dispatch.
     */
    private onDocumentMouseDown(e: MouseEvent): void {
        if (!this.pending) {
            return;
        }
        if (e.button !== 0) {
            return;
        }
        if (!e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) {
            return;
        }
        if (this.boundViewWithCanvas(e.target)) {
            // Accept: consume the click so the active tool does not
            // also start an action on this slice.
            e.preventDefault();
            e.stopPropagation();
            this.accept();
        } else if (this.isWithheldViewCanvas(e.target)) {
            e.preventDefault();
            e.stopPropagation();
            enqueueSnackbar(
                localization.t('contourAutoSuggestion.accept.primaryImageOnly'),
                { variant: 'warning' }
            );
        }
    }

    /**
     * After the engine handled a press (bubble phase): when it started
     * a brush stroke the ghost is withdrawn, so the stroke commits
     * only what it draws and Esc reaches it.
     */
    private onPressHandled(): void {
        if (this.pending && this.isBrushStrokeInProgress()) {
            this.discardPending();
            this.reevaluateAfterStroke = true;
        }
    }

    /**
     * Derives the suggestion again once the brush stroke that held it
     * off has ended: on release, or on a later move when the brush
     * abandons a stroke whose release it did not see.
     */
    private resumeAfterStroke(): void {
        if (this.reevaluateAfterStroke && !this.isBrushStrokeInProgress()) {
            this.reevaluateAfterStroke = false;
            this.scheduleEvaluation();
        }
    }

    /**
     * Drop any rendered ghost immediately on a contour edit, undo or
     * redo of the active structure, and schedule a fresh evaluation.
     * Nothing reads the texture here: on undo and redo the structure
     * restores its pixels in its own handler, which may run after this.
     */
    private onContourEdit(event: RoiContourEditEvent): void {
        if (!this.enabled || !this.getRoi) {
            return;
        }
        // ROI numbers are per structure set: gate on the bound ROI's owning
        // set too, so a same-numbered ROI elsewhere never drops the ghost.
        const roi = this.getRoi();
        if (
            !roi ||
            roi.roiNumber !== event.roiNumber ||
            roi.structureSetUid !== event.structureSetUid
        ) {
            return;
        }
        this.discardPending();
        this.scheduleEvaluation();
    }

    /**
     * Cancel any in-flight delay timer and queue a new evaluation.
     * Each slice change resets the timer so rapid scrolling never
     * triggers intermediate ghost renders — the suggestion only
     * appears once the user has stayed on the same slice for the
     * full {@link CONTOUR_AUTO_SUGGESTION_PREVIEW_DELAY_MS} window.
     */
    private scheduleEvaluation(): void {
        if (!this.enabled) {
            return;
        }
        this.cancelPendingTimer();
        // Drop any previously rendered ghost while waiting for the
        // next evaluation — it is bound to a different slice.
        this.discardPending();
        this.pendingTimer = setTimeout(() => {
            this.pendingTimer = undefined;
            this.evaluateForCurrentSlice();
        }, CONTOUR_AUTO_SUGGESTION_PREVIEW_DELAY_MS);
    }

    private cancelPendingTimer(): void {
        if (this.pendingTimer !== undefined) {
            clearTimeout(this.pendingTimer);
            this.pendingTimer = undefined;
        }
    }

    /**
     * Derive and render a suggestion for the visible slice; while a tool
     * action is in progress, only once it has ended.
     */
    private evaluateForCurrentSlice(): void {
        if (!this.enabled) {
            return;
        }
        if (this.isBrushStrokeInProgress()) {
            this.reevaluateAfterStroke = true;
            return;
        }
        const suggestion = this.deriveSuggestion();
        const grid = suggestion?.roi.texture?.textureGrid;
        // Emits vertices in voxel-centered continuous coordinates; the
        // renderer feeds those directly into `Grid.toWorldSpace`, which
        // applies the mm scaling (see extractSlicePolygons).
        const polygons =
            suggestion && grid
                ? extractSlicePolygons(
                      suggestion.sourceSliceBytes,
                      grid.sizeX,
                      grid.sizeY
                  )
                : [];
        const drawn = [...this.renderers.values()].some(Boolean);
        if (!suggestion || !grid || !drawn || !polygons.length) {
            this.discardPending();
            return;
        }
        const { roi, imageSliceZ, kind, sourceSliceBytes } = suggestion;
        const preview: ContourAutoSuggestionPreviewState = {
            polygonsVoxel: polygons,
            targetVoxelZ: suggestion.textureZ,
            textureGrid: grid,
            roiColor: lightenColor(roi.color, 0.15),
        };
        this.pending = { roi, imageSliceZ, kind, sourceSliceBytes, preview };
        this.paintRenderers(preview);
    }

    /**
     * The suggestion for the visible slice, if one is offered: beyond
     * the contoured range a copy of the nearest contoured slice, between
     * it a blend of the neighbouring slices. The range is located on
     * the pixel cache where it is populated, and on the GPU during a
     * tool action, which the cache lags; the slices the suggestion is
     * made from are read from the GPU via
     * {@link SignedDistanceField.sliceToArray}.
     */
    private deriveSuggestion(): DerivedSuggestion | undefined {
        const roi = this.getRoi?.();
        const image = this.environment?.image;
        const plane = this.displayedPlane();
        if (!plane || !roi?.canModifyContours || !image) {
            return undefined;
        }
        const texture = roi.texture;
        if (!texture || texture.isEmpty) {
            return undefined;
        }

        const sizeZ = texture.sizePx[2];
        const imageSliceZ = resolveImageSlice(image, plane);
        const resolved = resolveTextureSlice(texture, image, plane);
        if (
            imageSliceZ === undefined ||
            imageSliceZ < 0 ||
            imageSliceZ >= image.parent.grid.sizeZ ||
            resolved.kind === 'notASlicePlane'
        ) {
            return undefined;
        }
        const currentVoxelZ = resolved.sliceZ;

        const sdf = texture.signedDistanceField;
        const readSlice = (z: number): Uint8Array | undefined => {
            if (z < 0 || z >= sizeZ) {
                return undefined;
            }
            try {
                return sdf.sliceToArray(z);
            } catch (err) {
                console.warn(
                    'ContourAutoSuggestionController: sliceToArray failed',
                    err
                );
                return undefined;
            }
        };

        let position: ContouredRangePosition;
        try {
            // During a tool action the GPU runs ahead of the pixel cache.
            const hasContour = this.getActiveTool?.()?.isActionInProgress()
                ? (z: number) =>
                      ContourOperations.sliceHasContour(sdf.sliceToArray(z))
                : ContourOperations.contourPresenceReader(sdf);
            position = ContourOperations.locateInContouredRange(
                currentVoxelZ,
                sizeZ,
                hasContour
            );
        } catch {
            return undefined;
        }
        if (position.kind === 'beyond') {
            const extension = texture.textureGrid.zExtensionToHold(
                currentVoxelZ,
                texture.grid
            );
            const source = readSlice(position.nearestZ);
            return extension && source
                ? {
                      roi,
                      imageSliceZ,
                      kind: 'copied',
                      sourceSliceBytes: source,
                      textureZ: currentVoxelZ,
                      extension,
                  }
                : undefined;
        }
        if (position.kind !== 'between') {
            return undefined;
        }

        // Build the source slice using multi-slice B-spline
        // interpolation. We gather up to 4 non-empty neighbour
        // slices (z-2, z-1, z+1, z+2) and blend them with cubic
        // B-spline weights for smoother transitions compared to
        // the previous linear midpoint blend. When fewer slices
        // are available the function degrades gracefully.
        const candidateOffsets = [-2, -1, 1, 2];
        const neighbourSlices: SliceWithPosition[] = [];

        for (const offset of candidateOffsets) {
            const z = currentVoxelZ + offset;
            const slice = readSlice(z);
            if (slice && ContourOperations.sliceHasContour(slice)) {
                neighbourSlices.push({ data: slice, z });
            }
        }

        let sourceSlice: Uint8Array | undefined;
        if (neighbourSlices.length > 0) {
            try {
                sourceSlice = blendSlicesBSpline(
                    neighbourSlices,
                    currentVoxelZ
                );
            } catch (err) {
                console.warn(
                    'ContourAutoSuggestionController: B-spline blend failed, falling back to nearest copy',
                    err
                );
                // Fall back to the nearest neighbour
                sourceSlice = new Uint8Array(neighbourSlices[0].data);
            }
        }

        if (!sourceSlice) {
            return undefined;
        }

        const grid: Grid = texture.textureGrid;

        // Image-driven refinement: nudge the iso-surface toward
        // anatomical edges visible in the underlying image (works for
        // CT / PET / MR — see deriveRefinementOptions). The B-spline
        // blend gives smooth shape continuity along Z; the intensity-guided
        // passes add local boundary alignment. Failures here are
        // non-fatal — we keep the unrefined B-spline result so the
        // user always sees a preview.
        try {
            const intensitySlice = this.extractIntensitySliceForTexture(
                texture,
                currentVoxelZ
            );
            const refineOptions = intensitySlice
                ? this.deriveRefinementOptions(intensitySlice)
                : undefined;
            if (intensitySlice && refineOptions) {
                // mm→byte scale taken from the structure texture's own
                // quantization margin, so the refinement reflects this
                // SDF's actual encoding rather than a fixed default.
                const sdfBytesPerMm = texture.sdfBytesPerMm();
                // Pass 1: edge-based — pull iso toward intensity
                // gradients (anatomical edges).
                sourceSlice = refineSliceWithIntensityEdge(
                    sourceSlice,
                    intensitySlice,
                    grid.sizeX,
                    grid.sizeY,
                    sdfBytesPerMm,
                    refineOptions.edge
                );
                // Pass 2: region-based — contract/expand where the
                // absolute intensity clearly indicates interior vs
                // exterior (e.g. air inside a lung vs tissue on CT;
                // high uptake vs background on PET).
                sourceSlice = refineSliceWithIntensityRegion(
                    sourceSlice,
                    intensitySlice,
                    grid.sizeX,
                    grid.sizeY,
                    sdfBytesPerMm,
                    refineOptions.region
                );
            }
        } catch (err) {
            console.warn(
                'ContourAutoSuggestionController: intensity refinement failed, using B-spline result as-is',
                err
            );
        }

        return {
            roi,
            imageSliceZ,
            kind: 'interpolated',
            sourceSliceBytes: sourceSlice,
            textureZ: currentVoxelZ,
            extension: { before: 0, after: 0 },
        };
    }

    private discardPending(): void {
        // The renderers are cleared even with nothing pending, in case a
        // render was issued without the pending bookkeeping.
        this.pending = undefined;
        this.clearRenderers();
    }

    /**
     * Derive the refinement option overrides for the active
     * image's modality, scaling the intensity-unit thresholds so the
     * refinement works across CT / PET / MR rather than only CT.
     *
     *   - **CT** (calibrated Hounsfield Units): use the proven fixed
     *     defaults — return empty overrides.
     *   - **Non-CT** (PET activity/SUV, MR arbitrary units): scale
     *     intensity-unit thresholds via
     *     {@link scaleRefinementOptionsForSpread}.
     *
     * Returns `undefined` when a non-CT slice's intensity spread
     * cannot be characterised, signalling the caller to skip
     * refinement (keep the pure B-spline result) rather than chase
     * noise.
     */
    private deriveRefinementOptions(intensitySlice: Float32Array):
        | {
              edge: Partial<EdgeRefinementOptions>;
              region: Partial<RegionRefinementOptions>;
          }
        | undefined {
        const modality = this.environment?.image?.parent?.firstSlice.modality;

        // CT is calibrated — the intensity defaults are physically tuned.
        if (modality === undefined || modality === Modality.CT) {
            return { edge: {}, region: {} };
        }

        const spread = estimateRobustIntensitySpread(intensitySlice);
        if (spread === undefined) {
            return undefined;
        }

        return scaleRefinementOptionsForSpread(spread);
    }

    /**
     * Resolve the active image's intensity buffer + grid and delegate
     * the grid-aligned sampling to the shared
     * {@link extractIntensitySliceAlignedToGrid} helper (which both
     * this tool and the Interpolation tool use). Returns `undefined`
     * when no image is bound, the image has no float pixel data, the
     * grids are not voxel-aligned, or the target Z is outside the
     * volume.
     *
     * The float-precision intensity buffer lives on the underlying
     * {@link Image3D} (the un-transformed source); using the parent's
     * grid avoids any registration transform on secondary images and
     * keeps indexing consistent with the buffer layout.
     */
    private extractIntensitySliceForTexture(
        texture: ContourTexture,
        targetVoxelZ: number
    ): Float32Array | undefined {
        const image = this.environment?.image;
        const parent = image?.parent;
        if (!parent) {
            return undefined;
        }
        const floatData = parent.floatPixelData;
        if (!floatData) {
            return undefined;
        }
        return extractIntensitySliceAlignedToGrid(
            texture.textureGrid,
            parent.grid,
            floatData,
            targetVoxelZ
        );
    }

    /** Whether the brush is part-way through a stroke. */
    private isBrushStrokeInProgress(): boolean {
        const tool = this.getActiveTool?.();
        return tool instanceof BrushTool && tool.isActionInProgress();
    }
}

const sameBytes = (a: Uint8Array, b: Uint8Array): boolean =>
    a.length === b.length && a.every((value, i) => value === b[i]);
